const loadedSwiperScripts = new Map();
const loadedSwiperStyles = new Set();

function loadSwiperScript(src) {
  if (window.Swiper) {
    return Promise.resolve(window.Swiper);
  }

  if (!src) {
    return Promise.reject(new Error('Missing Swiper script source.'));
  }

  if (loadedSwiperScripts.has(src)) {
    return loadedSwiperScripts.get(src);
  }

  const existingScript = document.querySelector('script[data-swiper-slider-library]');
  const promise = new Promise((resolve, reject) => {
    const script = existingScript || document.createElement('script');

    if (window.Swiper) {
      resolve(window.Swiper);
      return;
    }

    script.addEventListener('load', () => resolve(window.Swiper), { once: true });
    script.addEventListener('error', () => reject(new Error(`Unable to load Swiper from ${src}`)), { once: true });

    if (!existingScript) {
      script.src = src;
      script.defer = true;
      script.dataset.swiperSliderLibrary = 'true';
      document.head.append(script);
    }
  });

  loadedSwiperScripts.set(src, promise);
  return promise;
}

function loadSwiperStyles(href) {
  if (!href || loadedSwiperStyles.has(href) || document.querySelector(`link[href="${href}"]`)) {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.swiperSliderStylesheet = 'true';
  document.head.append(link);
  loadedSwiperStyles.add(href);
}

class SwiperSlider extends HTMLElement {
  constructor() {
    super();

    this.swiper = null;
    this.mediaQueryList = null;
    this.handleMediaChange = this.handleMediaChange.bind(this);
  }

  connectedCallback() {
    this.configureMediaQuery();
    this.mount();
  }

  disconnectedCallback() {
    this.destroySwiper();

    if (this.mediaQueryList) {
      this.mediaQueryList.removeEventListener('change', this.handleMediaChange);
    }
  }

  configureMediaQuery() {
    if (this.mediaQueryList) {
      this.mediaQueryList.removeEventListener('change', this.handleMediaChange);
    }

    const config = this.getConfig();
    const query = config.deviceTarget === 'mobile' ? '(max-width: 749px)' : '(min-width: 750px)';

    this.mediaQueryList = config.deviceTarget === 'all' ? null : window.matchMedia(query);

    if (this.mediaQueryList) {
      this.mediaQueryList.addEventListener('change', this.handleMediaChange);
    }
  }

  handleMediaChange() {
    this.mount();
  }

  async mount() {
    const config = this.getConfig();

    if (!this.shouldEnable(config)) {
      this.destroySwiper();
      this.toggleAttribute('swiper-active', false);
      this.toggleAttribute('swiper-inactive', true);
      return;
    }

    const container = this.querySelector('[data-swiper-container]');

    if (!container) {
      return;
    }

    this.toggleAttribute('swiper-inactive', false);
    loadSwiperStyles(this.getAttribute('swiper-css'));

    try {
      const Swiper = await loadSwiperScript(this.getAttribute('swiper-src'));

      if (!this.isConnected || !this.shouldEnable(config)) {
        return;
      }

      this.destroySwiper();
      this.swiper = new Swiper(container, this.buildOptions(config));
      this.toggleAttribute('swiper-active', true);
    } catch (error) {
      console.error(error);
      this.toggleAttribute('swiper-active', false);
      this.toggleAttribute('swiper-inactive', true);
    }
  }

  shouldEnable(config) {
    if (config.deviceTarget === 'all') {
      return true;
    }

    return Boolean(this.mediaQueryList?.matches);
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  getConfig() {
    const configScript = this.querySelector('script[data-swiper-config]');

    if (configScript?.textContent) {
      try {
        return JSON.parse(configScript.textContent);
      } catch (error) {
        console.error(error);
      }
    }

    return {
      deviceTarget: this.getAttribute('device-target') || 'all',
      slidesPerView: {
        mobile: Number(this.getAttribute('slides-mobile')) || 1,
        tablet: Number(this.getAttribute('slides-tablet')) || 2,
        desktop: Number(this.getAttribute('slides-desktop')) || 4,
      },
      spaceBetween: Number(this.getAttribute('space-between')) || 16,
      showArrows: this.hasAttribute('show-arrows'),
      showPagination: this.hasAttribute('show-pagination'),
      showProgress: this.hasAttribute('show-progress'),
      autoplay: this.hasAttribute('autoplay'),
      autoplayDelay: Number(this.getAttribute('autoplay-delay')) || 3000,
    };
  }

  buildOptions(config) {
    const options = {
      a11y: {
        containerMessage: 'Product carousel',
        nextSlideMessage: 'Next slide',
        prevSlideMessage: 'Previous slide',
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      slidesPerView: config.slidesPerView.mobile,
      spaceBetween: config.spaceBetween,
      watchOverflow: true,
      breakpoints: {
        750: {
          slidesPerView: config.slidesPerView.tablet,
          spaceBetween: config.spaceBetween,
        },
        990: {
          slidesPerView: config.slidesPerView.desktop,
          spaceBetween: config.spaceBetween,
        },
      },
    };

    const nextButton = this.querySelector('[data-swiper-next]');
    const previousButton = this.querySelector('[data-swiper-prev]');

    if (config.showArrows && nextButton && previousButton) {
      options.navigation = {
        nextEl: nextButton,
        prevEl: previousButton,
      };
    }

    const pagination = this.querySelector('[data-swiper-pagination]');

    if (config.showPagination && pagination) {
      options.pagination = {
        el: pagination,
        clickable: true,
      };
    }

    const progress = this.querySelector('[data-swiper-progress]');

    if (config.showProgress && progress) {
      options.scrollbar = {
        el: progress,
        draggable: false,
      };
    }

    if (config.autoplay) {
      options.autoplay = {
        delay: config.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    return options;
  }
}

if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider);
}
